import { Injectable } from '@angular/core'
import { HttpStatusCode } from '@angular/common/http'
import { Clipboard } from '@angular/cdk/clipboard'
import { MatDialog } from '@angular/material/dialog'
import { Observable, of } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { CallerService } from './caller.service'
import { AnalyticsService } from './analytics.service'
import { SaleType } from '../ngrx-store/app.state'
import { deletePostedPost, newMessage, saleSaveChanged, saleSoldChanged } from '../ngrx-store/actions/corp-member.actions'
import { chatIdentityOf } from '../utils/corp-member.util'
import { LoginSignupPromptComponent } from '../dialogs/login-signup-prompt/login-signup-prompt.component'
import { DeletePostComponent } from '../dialogs/delete-post/delete-post.component'

/** The public link to a sale - what Share sends and what a chat about it points to. */
export function saleUrl(sale: Pick<SaleType, 'id' | 'slug_id'>): string {
  return `${window.location.origin}/s/${sale.slug_id ?? sale.id}`
}

/**
 * What you can do with a sale - save, share, chat with the seller, and (your own) mark it sold or
 * delete it. Shared by the sale card and the item page (/s/:id), and keeps the store up to date
 * so every card showing the same sale agrees.
 */
@Injectable({
  providedIn: 'root',
})
export class SaleActionsService {
  constructor(
    private callerService: CallerService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private analyticsService: AnalyticsService,
  ) {}

  isOwnSale(sale: SaleType | undefined): boolean {
    return Boolean(sale?.corp_member_id) && sale?.corp_member_id === this.callerService.corpMember?.id
  }

  /** Saves (bookmarks) the sale, or removes it from saved. Logged out, it asks them to log in. Emits whether it's saved now. */
  toggleSave(sale: SaleType, saved: boolean): Observable<boolean> {
    if (!this.callerService.isLoggedIn) {
      this.openLoginPrompt()
      return of(saved)
    }

    const request$ = saved ? this.callerService.unBookmarkSale(sale.id) : this.callerService.bookmarkSale(sale.id)
    return request$.pipe(
      map(() => !saved),
      tap((nowSaved) => {
        this.callerService.store.dispatch(saleSaveChanged({ id: sale.id, saved: nowSaved }))
        this.callerService.showNotification(nowSaved ? 'Saved. Find it on your dashboard.' : 'Removed from saved.')
      }),
      catchError((err) => {
        this.callerService.showNotification(
          err?.error?.message ?? (saved ? 'Couldn\'t remove it from saved. Please try again.' : 'Couldn\'t save it. Please try again.'),
        )
        return of(saved)
      }),
    )
  }

  /** The phone's share sheet (WhatsApp and the rest) where there is one, else the link is copied. */
  async share(sale: SaleType): Promise<void> {
    const url = saleUrl(sale)
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: sale.item_name, text: `${sale.item_name} - ₦${sale._price} on corpers.ng`, url })
        this.analyticsService.saleShared('share_sheet')
      } catch (err) {
        // AbortError: they closed the sheet. Anything else, fall back to copying.
        if ((err as DOMException)?.name !== 'AbortError') this.copyLink(url)
      }
      return
    }
    this.copyLink(url)
  }

  private copyLink(url: string): void {
    const copied = this.clipboard.copy(url)
    if (copied) this.analyticsService.saleShared('copy_link')
    this.callerService.showNotification(copied ? 'Link copied - paste it anywhere to share.' : 'Couldn\'t copy the link. Please try again.')
  }

  /**
   * Opens a chat with the seller, with a first message about this item typed in for them (the link
   * tells the seller which one). Logged out, it asks them to log in.
   */
  chatWithSeller(sale: SaleType): void {
    if (!this.callerService.isLoggedIn) {
      this.openLoginPrompt()
      return
    }

    const recipientId = chatIdentityOf(sale.CorpMember)
    const initiatorId = chatIdentityOf(this.callerService.corpMember)
    if (!recipientId || !initiatorId) {
      this.callerService.showNotification('Sorry, we couldn\'t find the seller.')
      return
    }

    this.analyticsService.chatStarted('sale')
    const sellerName = sale.CorpMember?.first_name ?? sale.CorpMember?.nickname
    this.callerService.store.dispatch(newMessage({
      recipient_id: recipientId,
      recipient_state_code: sale.CorpMember?.state_code ?? '',
      recipient_name: sellerName,
      initiator_state_code: this.callerService.corpMember.state_code ?? '',
      initiator_name: this.callerService.corpMember.first_name,
      initiator_id: initiatorId,
      // Listings don't include the poster's state code any more (it's a login identifier), so
      // the chat starts with their name; the chat list fills the state code in from /chat.
      chat_display_name: sellerName,
      chat_display_state_code: sale.CorpMember?.state_code,
      source: 'sale',
      draft: `Hi, is your ${sale.item_name} (₦${sale._price}) still available? ${saleUrl(sale)}`,
      listing: sale.slug_id ? { listing_type: 'sale', listing_slug: sale.slug_id } : undefined,
    }))
  }

  /** Your own sale: sold, or back on sale. Emits the new `sold_at`, or undefined if it failed. */
  setSold(sale: SaleType, sold: boolean): Observable<string | null | undefined> {
    return this.callerService.setSaleSold(sale.id, sold).pipe(
      map((res) => res.body?.data?.sold_at ?? null),
      tap((sold_at) => {
        this.callerService.store.dispatch(saleSoldChanged({ id: sale.id, sold_at }))
        if (sold) this.analyticsService.saleMarkedSold()
        this.callerService.showNotification(sold ? 'Marked as sold. Buyers can see it for a day, then it leaves the feed.' : 'It\'s back on sale.')
      }),
      catchError((err) => {
        this.callerService.showNotification(err?.error?.message ?? 'Couldn\'t update it. Please try again.')
        return of(undefined)
      }),
    )
  }

  /** Asks first, then deletes your own sale. */
  confirmDelete(sale: SaleType): void {
    this.dialog
      .open(DeletePostComponent, { maxWidth: '360px', data: { sale } })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'delete') this.delete(sale)
      })
  }

  private delete(sale: SaleType): void {
    this.callerService.deletePostedItem({ id: sale.id, type: 'sale' }).subscribe({
      next: (res) => {
        if (res.status === HttpStatusCode.Ok) {
          this.callerService.store.dispatch(deletePostedPost({ id: `sale_${sale.id}` }))
          this.callerService.offerUndoDelete({ id: sale.id, type: 'sale' }, 'Sale deleted.')
        }
      },
      error: (err) => this.callerService.showNotification(err?.error?.message ?? 'Couldn\'t delete it. Please try again.'),
    })
  }

  openLoginPrompt(): void {
    this.dialog.open(LoginSignupPromptComponent, { maxWidth: '360px', data: {} })
  }
}
